import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import profileService from '../../services/profile.service';
import './ResumeUpload.css';

interface ResumeUploadProps {
  darkMode?: boolean;
  onUploadComplete?: () => void;
  hideHeader?: boolean;
  customTitle?: string;
  customDescription?: string;
}

const ResumeUpload: React.FC<ResumeUploadProps> = ({ 
  darkMode = false, 
  onUploadComplete,
  hideHeader = false,
  customTitle,
  customDescription
}) => {
  const { user, updateProfile } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file: File) => {
    setError(null);
    
    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      // Upload the file using the profile service
      const result = await profileService.uploadResume(file);

      // Update user profile with resume info
      await updateProfile({
        resume: {
          url: result.url,
          uploadedAt: result.uploadedAt
        }
      });
      
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (err) {
      setError('Failed to upload resume. Please try again.');
      console.error('Resume upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const onButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`resume-upload ${darkMode ? 'dark' : 'light'}`}>
      {!hideHeader && (
        <div className="upload-header">
          <h3>{customTitle || 'Upload Your Resume'}</h3>
          <p>{customDescription || 'Upload your resume to unlock personalized job recommendations'}</p>
        </div>
      )}

      {user?.resume ? (
        <div className="current-resume">
          <div className="resume-info">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="resume-details">
              <p className="resume-filename">Resume uploaded</p>
              <p className="resume-date">
                Updated {new Date(user.resume.uploadedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <button 
            className="replace-btn"
            onClick={onButtonClick}
            disabled={uploading}
          >
            Replace Resume
          </button>
        </div>
      ) : (
        <form 
          className={`upload-form ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            ref={fileInputRef}
            type="file"
            id="file-upload"
            accept=".pdf,.doc,.docx"
            onChange={handleChange}
            style={{ display: 'none' }}
          />
          <label 
            htmlFor="file-upload" 
            className="upload-label"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="upload-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M36 32v6a2 2 0 01-2 2H14a2 2 0 01-2-2v-6M32 22l-8-8-8 8M24 30V14" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="upload-text">
              {dragActive ? (
                'Drop your resume here'
              ) : (
                <>
                  Drag and drop your resume, or <span className="browse-link">browse</span>
                </>
              )}
            </p>
            <p className="upload-hint">Supports PDF, DOC, DOCX (max 5MB)</p>
          </label>
        </form>
      )}

      {error && (
        <div className="upload-error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 14A6 6 0 108 2a6 6 0 000 12zM8 5v3M8 11h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {error}
        </div>
      )}

      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
          <p>Uploading resume...</p>
        </div>
      )}
    </div>
  );
};

export default ResumeUpload;